import { Button, Grid } from '@material-ui/core';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../core/store';
import * as settings from '../../hooks/useSettings';
import useStyles from '../../styles/useStyles';
import useSecurity from './../../hooks/useSecurity/useSecurity';
import { ActionConfirmationDialogComponent } from '../../ui-kit';
import RelaticsSyncLogsGrid from './RelaticsSyncLogsGrid';
import { useForceSync } from '../../hooks/useForceSync';
import { State } from '@progress/kendo-data-query';
import getDefaultGridState from '../../../core/types/common/DefaultGridState';
import { HubConnection } from '@microsoft/signalr';
import { useToastify } from '../../../../Hooks';

interface RelaticsSyncLogsProps {
    connection: HubConnection | undefined;
}

const RelaticsSyncLogs: React.FC<RelaticsSyncLogsProps> = ({ connection }) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    const [startDate, setStartDate] = useState(useAppSelector(settings.selectors.getStartDate));
    const [endDate, setEndDate] = useState(useAppSelector(settings.selectors.getEndDate));

    const [gridState, setGridState] = useState<State>(getDefaultGridState());
    const { showToastError } = useToastify();

    useEffect(() => {
        setGridState(getDefaultGridState());
    }, [startDate, endDate]);

    const { canManage } = useSecurity();

    const { handleOnClick, forceSyncDialogActive, setForceSyncDialogActive } = useForceSync();
    const handleOnClickForceSync = () => {
        handleOnClick('', setGridState);
    };

    const handleOnChangeStartDate = (date: Date | null) => {
        if (date === null) {
            setStartDate(undefined);
        } else {
            if (date!.getDate() < 1) return;
            if (date!.getFullYear() < 1) return;
            setStartDate(date);
        }
    };

    const refreshGridWithNewStartDate = () => {
        if (startDate === undefined) {
            dispatch(settings.actions.setStartDate(undefined));
        } else {
            if (endDate !== undefined) {
                if (startDate > endDate) {
                    showToastError('Start date must be equal or lower than End Date!');
                    return;
                }
            }
            dispatch(settings.actions.setStartDate(startDate));
        }
    };

    const handleOnChangeEndDate = (date: Date | null) => {
        if (date === null) {
            setEndDate(undefined);
        } else {
            setEndDate(date);
        }
    };

    const refreshGridWithNewEndDate = () => {
        if (endDate === undefined) {
            dispatch(settings.actions.setEndDate(undefined));
        } else {
            if (startDate !== undefined) {
                if (startDate > endDate) {
                    dispatch(settings.actions.setEndDate(endDate));
                    showToastError('End date must be equal or greater than Start Date!');
                    return;
                }
            }
            dispatch(settings.actions.setEndDate(endDate));
        }
    };

    return (
        <>
            <ActionConfirmationDialogComponent
                dialogActive={forceSyncDialogActive}
                setDialogActive={setForceSyncDialogActive}
                handleOnOk={handleOnClickForceSync}
                text={'Are you sure you want to start force sync ?'}
            />
            <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                <Grid style={{ display: 'flex', flexDirection: 'row' }}>
                    <Grid item xs={2} style={{ display: 'flex', flexDirection: 'row' }}>
                        <Grid item>
                            <span style={{ marginLeft: '8px' }}>Start Date</span>
                            <DateTimePicker
                                className={classes.dateTimePicker}
                                format={'dd/MM/yyyy HH:mm'}
                                value={startDate}
                                onChange={(e) => handleOnChangeStartDate(e.value)}
                                onBlur={() => refreshGridWithNewStartDate()}
                            />
                        </Grid>
                        <Grid item>
                            <span>End Date</span>
                            <DateTimePicker
                                format={'dd/MM/yyyy HH:mm'}
                                value={endDate}
                                min={startDate}
                                onChange={(e) => handleOnChangeEndDate(e.value)}
                                onBlur={() => refreshGridWithNewEndDate()}
                            />
                        </Grid>
                    </Grid>
                    {canManage() && (
                        <Grid item xs={12} className={classes.forceSyncGrid}>
                            <Button
                                className={classes.forceSyncButton}
                                color="primary"
                                variant="contained"
                                size="small"
                                onClick={() => setForceSyncDialogActive(!forceSyncDialogActive)}
                            >
                                Force sync
                            </Button>
                        </Grid>
                    )}
                </Grid>
                <Grid item xs={12} className={classes.syncLogsGrid}>
                    <RelaticsSyncLogsGrid gridState={gridState} setGridState={setGridState} connection={connection} />
                </Grid>
            </Grid>
        </>
    );
};

export default RelaticsSyncLogs;
